import { HttpErrorResponse } from '@angular/common/http';
import { createSelector } from 'reselect';
import { Action } from '@ngrx/store';
import { AppState } from '../app.state';
import { KioskAppointment, KioskPatient } from './kiosk.model';
import * as kioskActions from './kiosk.actions';

export enum KioskError { Hin, HttpError, NoAppointments, InvalidInformations };
export enum KioskTimer { Active, Inactive };

export interface State {
  readonly subscriptionId: number;
  readonly userHin: string;
  readonly locale: 'en' | 'fr_CA';
  readonly fetching: boolean;
  readonly appointments: KioskAppointment[];
  readonly patients: KioskPatient[];
  readonly timer: KioskTimer;
  readonly error: KioskError;
}

const initialState: State = {
  subscriptionId: null,
  userHin: null,
  locale: null,
  fetching: false,
  appointments: [],
  patients: [],
  timer: KioskTimer.Inactive,
  error: null,
};

export function kioskReducer(state = initialState, action: Action): State {
  switch (action.type) {
    case kioskActions.ActionTypes.FETCH_KIOSK:
      return {
        ...state,
        fetching: true,
      };

    case kioskActions.ActionTypes.ADD_KIOSK_APPOINTMENT:
      const { appointments } = state;

      return {
        ...state,
        appointments: [
          ...action.payload.reduce((prev: KioskAppointment[], next: KioskAppointment): KioskAppointment[] => {
            if (!!prev.find((appointment: KioskAppointment): boolean => appointment.appointmentId === next.appointmentId)) {
              return prev;
            }
            return [...prev, next];
          }, appointments),
        ],
      };

    case kioskActions.ActionTypes.ADD_KIOSK_PATIENT:
      const { patients } = state;
  
      return {
        ...state,
        patients: [
          ...action.payload.reduce((prev: KioskPatient[], next: KioskAppointment): KioskPatient[] => {
            const { profile, patientName, patientId } = next;

            if (!!prev.find((patient: KioskPatient): boolean => patient.patientId === patientId)) {
              return prev;
            }

            return [
              ...prev,
              {
                name: patientName,
                profile,
                patientId,
              },
            ];
          }, patients),
        ],
      };

    case kioskActions.ActionTypes.SET_KIOSK_LOCALE:
      return {
        ...state,
        locale: action.payload,
      };

    case kioskActions.ActionTypes.SET_KIOSK_USER_HIN:
      return {
        ...state,
        userHin: action.payload,
      };

    case kioskActions.ActionTypes.SET_KIOSK_ERROR:
      return {
        ...state,
        error: action.payload,
      };

    case kioskActions.ActionTypes.SET_KIOSK_SUBSCRIPTION_ID:
      return {
        ...state,
        subscriptionId: action.payload,
      };

    case kioskActions.ActionTypes.SET_KIOSK_FETCHING_STATE:
      return {
        ...state,
        fetching: action.payload,
      };

    case kioskActions.ActionTypes.START_KIOSK_TIMER:
      return {
        ...state,
        timer: KioskTimer.Active,
      };

    case kioskActions.ActionTypes.STOP_KIOSK_TIMER:
      return {
        ...state,
        timer: KioskTimer.Inactive,
      };

    case kioskActions.ActionTypes.RESET_KIOSK:
      return {
        ...state,
        ...initialState,
      };

    case kioskActions.ActionTypes.CONFIRM_KIOSK_APPOINTMENT:
      return {
        ...state,
        appointments: state.appointments.map((appointment: KioskAppointment) => {
          if (appointment.appointmentId === action.payload) {
            return {
              ...appointment,
              arrived: true,
            }
          }

          return appointment;
        }),
      };

    default:
      return state;
  }
}

export const getKioskState = (state: AppState) => state.kiosk;

export const getAppointments = createSelector(getKioskState, (state: State): KioskAppointment[] => state.appointments);
export const getPatients = createSelector(getKioskState, (state: State): KioskPatient[] => state.patients);
export const getLocale = createSelector(getKioskState, (state: State): string => state.locale);
export const getUserHin = createSelector(getKioskState, (state: State): string => state.userHin);
export const getSubscriptionId = createSelector(getKioskState, (state: State): number => state.subscriptionId);
export const getError = createSelector(getKioskState, (state: State): KioskError => state.error);
export const getTimer = createSelector(getKioskState, (state: State): KioskTimer => state.timer);
export const isFetching = createSelector(getKioskState, (state: State): boolean => state.fetching);



// WEBPACK FOOTER //
// ../node_modules/angular2-template-loader!./ng2/kiosk/kiosk.reducer.ts